import { ref } from 'vue'
import { trackEvent } from '~/composables/analytics'
import { buildPaletteFile, paletteFileBaseName } from '~/utils/paletteFile'
import { response } from '~/assets/js/colorData.js'

// Palette export / download. The format `type` + `format` tokens and the
// Clarity "Download File (<ext>)" event MUST stay exactly as they were — they
// are the analytics keys the dashboards depend on. `onError` lets the page
// surface a failure the same way the old inline handler did.
//
// `source` is the site the palette was generated from (the page's `typedUrl`);
// it only feeds the download filename, which is why it's a getter rather than a
// required ref — without it the file is still named, just without the domain.
export function usePaletteExport({
  onError,
  source,
}: { onError?: (error: unknown) => void; source?: () => string } = {}) {
  const isDownloadModalOpen = ref(false)
  const isDownloading = ref(false)

  // The file is built here in the browser (utils/paletteFile.ts) instead of
  // being fetched from the legacy `POST https://api.colorize.design/saveFile`.
  // Export is a pure transform of data the page already holds, so the round
  // trip bought nothing: the download now works offline, cannot fail on a cold
  // VPS, and keeps the palette off a third-party server. The same module backs
  // `POST /api/saveFile`, which keeps that contract available to external
  // callers on our own domain.

  // Palette export formats, grouped by the download-modal layout. `type` is the
  // backend format name; `format` is the file extension AND the token in the
  // Clarity "Download File (<ext>)" event, so these values must stay exact.
  const primaryFormats = [
    { type: 'CSS_VARIABLES', format: 'css', label: 'CSS' },
    { type: 'SVG', format: 'svg', label: 'SVG' },
  ]
  const adobeFormats = [
    { type: 'ADOBE_SWATCH_EXCHANGE', format: 'ase', label: 'Adobe Swatch' },
    { type: 'ADOBE_COLOR_TABLE_PALETTE', format: 'act', label: 'Adobe Color Table' },
    { type: 'ADOBE_COLOR_SWATCH', format: 'aco', label: 'Adobe Color Swatch' },
  ]
  const sketchFormat = { type: 'SKETCH_PALETTE', format: 'sketchpalette', label: 'Sketch Palette' }
  const otherFormats = [
    { type: 'SASS_VARIABLES', format: 'sass', label: 'SASS' },
    { type: 'HOMESITE_PALETTE', format: 'hpl', label: 'Homesite Palette' },
    { type: 'SCSS_VARIABLES', format: 'scss', label: 'SCSS' },
    { type: 'GIMP_PALETTE', format: 'gpl', label: 'GIMP Palette' },
    { type: 'PAINTDOTNET_PALETTE', format: 'txt', label: 'PAINT.NET Palette' },
    { type: 'PAINT_SHOP_PRO_PALETTE', format: 'pal', label: 'Paint Shop Pro Palette' },
    { type: 'STAROFFICE_PALETTE', format: 'soc', label: 'Staroffice Palette' },
    { type: 'SKENCIL_PALETTE', format: 'spl', label: 'Skencil Palette' },
  ]

  // NOTE: the shared card styling lives in DownloadPaletteModal.vue, not here.
  // Tailwind only scans components/pages/layouts, so a class string kept in this
  // .ts file would be purged from the build.

  const downloadFile = async (
    { type, category, format }: { type: string; category?: string; format: string },
    waitUntil?: Promise<unknown>,
  ) => {
    const formatName = type
    const extension = format
    // Branded, source-aware name: `colorize.design-stripe.com-palette.css`.
    // `category` stays supported as an explicit override for any other caller.
    const fileName = category || paletteFileBaseName(formatName, source?.())

    try {
      try {
        trackEvent('Download File (' + extension + ')')
      } catch (error) {}

      // `response` is the reactive palette the page is displaying; the export
      // covers the whole palette, not just the selected swatches, as before.
      const file = await buildPaletteFile(formatName, JSON.stringify(response))
      const blob = new Blob([file.content], { type: file.mimeType })
      // Hold the browser's save dialog until the minimum loader time has elapsed,
      // so the file explorer only opens after the 4s.
      if (waitUntil) {
        try {
          await waitUntil
        } catch (e) {}
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = `${fileName}.${extension}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      if (onError) onError(error)
    }
  }

  const selectDownload = async (fmt: { type: string; format: string }) => {
    if (isDownloading.value) return
    // Swap the buttons for a loader while the file is prepared. The request starts now
    // (downloadFile fires the Clarity event + runs the export), but the save dialog is
    // held until this 4s minimum has elapsed. Longer requests add no extra wait.
    isDownloading.value = true
    const minVisible = new Promise((resolve) => setTimeout(resolve, 4000))
    // No `category`: the name comes from the brand + the generated site.
    await downloadFile({ type: fmt.type, format: fmt.format }, minVisible)
    isDownloading.value = false
    isDownloadModalOpen.value = false
  }

  return {
    isDownloadModalOpen,
    isDownloading,
    primaryFormats,
    adobeFormats,
    sketchFormat,
    otherFormats,
    selectDownload,
  }
}
